
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CertificateNumberInventory } from '@/utils/certificateInventoryTypes';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { createCertificateAuditRecord } from '@/utils/auditUtils';
import { CertificateStatusBadge } from './CertificateStatusBadge';
import { CertificateVerificationModal } from './CertificateVerificationModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, FileCheck, FileX, Loader2, MoreHorizontal, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CertificatesInventoryTableProps {
  data?: CertificateNumberInventory[];
  isLoading?: boolean;
  error?: Error | null;
  filters?: {
    state?: string;
    courseType?: string;
    reportingStatus?: string;
    searchQuery?: string;
  };
  onRefresh?: () => void;
  onCertificateSelect?: (certificateNumber: string) => void;
}

export function CertificatesInventoryTable({ 
  data, 
  isLoading, 
  error,
  filters,
  onRefresh,
  onCertificateSelect
}: CertificatesInventoryTableProps) {
  const { toast } = useToast();
  const [selectedCertificates, setSelectedCertificates] = useState<string[]>([]);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateNumberInventory | null>(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // If no data is provided, fetch it using the filters
  const {
    data: fetchedData,
    isLoading: isLoadingData,
    error: fetchError,
    refetch
  } = useQuery({
    queryKey: ['certificateInventory', filters],
    queryFn: async () => {
      let query = supabase
        .from('certificate_number_inventory')
        .select('*, certificate_reports(*)')
        .order('created_at', { ascending: false });
      
      if (filters?.state) {
        query = query.eq('state', filters.state);
      }
      
      if (filters?.courseType) {
        query = query.eq('course_type', filters.courseType);
      }
      
      if (filters?.reportingStatus) {
        query = query.eq('reporting_status', filters.reportingStatus);
      }
      
      if (filters?.searchQuery) {
        query = query.ilike('certificate_number', `%${filters.searchQuery}%`);
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) throw error;
      return data as CertificateNumberInventory[];
    },
    enabled: !data, // Only run query if no data is provided
  });

  // Use provided data or fetched data
  const certificates = data || fetchedData;
  const loadingState = isLoading !== undefined ? isLoading : isLoadingData;
  const errorState = error || fetchError;

  const handleSelect = (certificateId: string) => {
    if (selectedCertificates.includes(certificateId)) {
      setSelectedCertificates(selectedCertificates.filter(id => id !== certificateId));
    } else {
      setSelectedCertificates([...selectedCertificates, certificateId]);
    }
  };

  const handleSelectAll = () => {
    if (certificates && certificates.length > 0) {
      if (selectedCertificates.length === certificates.length) {
        setSelectedCertificates([]);
      } else {
        setSelectedCertificates(certificates.map(cert => cert.id));
      }
    }
  };

  const openVerifyModal = (certificate: CertificateNumberInventory) => {
    setSelectedCertificate(certificate);
    setIsVerifyModalOpen(true);
  };

  const handleVerificationComplete = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      refetch();
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      refetch();
    }
  };

  const handleViewDetails = async (certificate: CertificateNumberInventory) => {
    if (onCertificateSelect) {
      // Create an audit record for viewing the certificate
      await createCertificateAuditRecord({
        certificateNumber: certificate.certificate_number,
        inventoryId: certificate.id,
        actionType: 'viewed',
        details: 'Viewed from inventory table'
      });
      
      // Open the certificate detail view
      onCertificateSelect(certificate.certificate_number);
    }
  };

  const handleBatchVerify = async () => {
    if (selectedCertificates.length === 0) return;

    setIsBatchProcessing(true);
    try {
      // Implement batch verification logic here
      // For demo purposes just showing a toast
      toast({
        title: 'Batch Verification',
        description: `Verifying ${selectedCertificates.length} certificates. This functionality will be implemented soon.`
      });
      
      // Wait a bit to simulate processing
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Reset selection
      setSelectedCertificates([]);
      handleRefresh();
    } catch (error) {
      console.error('Error in batch processing:', error);
      toast({
        title: 'Batch Processing Failed',
        description: 'An error occurred while processing the certificates.',
        variant: 'destructive'
      });
    } finally {
      setIsBatchProcessing(false);
    }
  };

  // Show loading state
  if (loadingState) {
    return (
      <div className="w-full py-10 flex justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show error state
  if (errorState) {
    return (
      <div className="w-full py-10 text-center">
        <p className="text-red-500">Error loading certificate data</p>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          className="mt-4"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedCertificates.length > 0 && (
        <div className="flex items-center justify-between bg-muted p-2 rounded-md">
          <p className="text-sm">{selectedCertificates.length} certificates selected</p>
          <div className="flex gap-2">
            <Button 
              size="sm"
              variant="outline"
              onClick={() => setSelectedCertificates([])}
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleBatchVerify}
              disabled={isBatchProcessing}
            >
              {isBatchProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FileCheck className="h-4 w-4 mr-2" />
                  Batch Verify
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    certificates && 
                    certificates.length > 0 && 
                    selectedCertificates.length === certificates.length
                  }
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all certificates"
                />
              </TableHead>
              <TableHead>Certificate #</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Course Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reporting</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certificates && certificates.length > 0 ? (
              certificates.map((certificate) => (
                <TableRow key={certificate.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedCertificates.includes(certificate.id)}
                      onCheckedChange={() => handleSelect(certificate.id)}
                      aria-label={`Select certificate ${certificate.certificate_number}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatCertificateNumber(certificate.certificate_number)}
                  </TableCell>
                  <TableCell>{certificate.state}</TableCell>
                  <TableCell>{certificate.course_type}</TableCell>
                  <TableCell>
                    {certificate.is_used ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Used
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Available
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <CertificateStatusBadge type="reporting" item={certificate} />
                  </TableCell>
                  <TableCell>
                    {certificate.assigned_at 
                      ? new Date(certificate.assigned_at).toLocaleDateString() 
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleViewDetails(certificate)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openVerifyModal(certificate)}>
                          <FileCheck className="h-4 w-4 mr-2" />
                          Verify
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <FileX className="h-4 w-4 mr-2" />
                          Mark as Not Used
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No certificates found. Try adjusting your filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      <CertificateVerificationModal
        certificate={selectedCertificate}
        open={isVerifyModalOpen}
        onOpenChange={setIsVerifyModalOpen}
        onVerificationComplete={handleVerificationComplete}
      />
    </div>
  );
}
